import { useTranslation } from 'react-i18next';
import { Plus, Trash2, GripVertical, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import type { CustomQuestion } from '@/types/booking';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';

export interface CustomQuestionsEditorProps {
  questions: CustomQuestion[];
  onChange: (questions: CustomQuestion[]) => void;
  disabled?: boolean;
}

export function CustomQuestionsEditor({ questions, onChange, disabled = false }: CustomQuestionsEditorProps) {
  const { t } = useTranslation(['services', 'common']);
  const { toast } = useToast();

  const addQuestion = () => {
    if (questions.length >= 3) {
      toast({
        title: t('services:customQuestions.maxReached'),
        description: t('services:customQuestions.maxReachedDescription'),
        variant: 'destructive',
      });
      return;
    }

    const newQuestion: CustomQuestion = {
      id: crypto.randomUUID(),
      question: '',
      type: 'text',
      required: false,
      order: questions.length,
    };

    onChange([...questions, newQuestion]);
  };

  const removeQuestion = (id: string) => {
    const updatedQuestions = questions
      .filter(q => q.id !== id)
      .map((q, index) => ({ ...q, order: index }));
    onChange(updatedQuestions);
  };

  const updateQuestion = (id: string, updates: Partial<CustomQuestion>) => {
    onChange(questions.map(q =>
      q.id === id ? { ...q, ...updates } : q
    ));
  };

  const addOption = (questionId: string) => {
    const question = questions.find(q => q.id === questionId);
    if (!question) return;

    const options = question.options || [];
    updateQuestion(questionId, { options: [...options, ''] });
  };

  const updateOption = (questionId: string, optionIndex: number, value: string) => {
    const question = questions.find(q => q.id === questionId);
    if (!question || !question.options) return;

    const options = [...question.options];
    options[optionIndex] = value;
    updateQuestion(questionId, { options });
  };

  const removeOption = (questionId: string, optionIndex: number) => {
    const question = questions.find(q => q.id === questionId);
    if (!question || !question.options) return;

    const options = question.options.filter((_, i) => i !== optionIndex);
    updateQuestion(questionId, { options });
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(questions);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update order field
    const reorderedQuestions = items.map((q, index) => ({ ...q, order: index }));
    onChange(reorderedQuestions);
  };

  return (
    <div className="space-y-4">
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="questions" isDropDisabled={disabled}>
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className="space-y-4"
            >
              {questions.map((question, index) => (
                <Draggable
                  key={question.id}
                  draggableId={question.id}
                  index={index}
                  isDragDisabled={disabled}
                >
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className={`border rounded-lg p-4 bg-white ${
                        snapshot.isDragging ? 'shadow-lg' : ''
                      } ${disabled ? 'opacity-60' : ''}`}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          {...provided.dragHandleProps}
                          className={`mt-8 ${disabled ? 'cursor-not-allowed' : 'cursor-grab active:cursor-grabbing'}`}
                        >
                          <GripVertical className="h-5 w-5 text-gray-400" />
                        </div>

                        <div className="flex-1 space-y-4">
                          <div className="flex items-center justify-between">
                            <Label className="text-base font-semibold">
                              {t('services:customQuestions.questionNumber', { number: index + 1 })}
                            </Label>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeQuestion(question.id)}
                              disabled={disabled}
                              className="h-8 w-8 p-0"
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor={`question-${question.id}`}>
                                {t('services:customQuestions.questionLabel')}
                              </Label>
                              <Input
                                id={`question-${question.id}`}
                                value={question.question}
                                onChange={(e) => updateQuestion(question.id, { question: e.target.value })}
                                placeholder={t('services:customQuestions.questionPlaceholder')}
                                disabled={disabled}
                                className="h-10"
                              />
                            </div>

                            <div className="space-y-2">
                              <Label htmlFor={`type-${question.id}`}>
                                {t('services:customQuestions.typeLabel')}
                              </Label>
                              <Select
                                value={question.type}
                                onValueChange={(value: 'text' | 'select' | 'checkbox') =>
                                  updateQuestion(question.id, { type: value })
                                }
                                disabled={disabled}
                              >
                                <SelectTrigger id={`type-${question.id}`} className="h-10">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="text">{t('services:customQuestions.typeText')}</SelectItem>
                                  <SelectItem value="select">{t('services:customQuestions.typeSelect')}</SelectItem>
                                  <SelectItem value="checkbox">{t('services:customQuestions.typeCheckbox')}</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          <div className="flex items-center space-x-2">
                            <Switch
                              id={`required-${question.id}`}
                              checked={question.required}
                              onCheckedChange={(checked) =>
                                updateQuestion(question.id, { required: checked })
                              }
                              disabled={disabled}
                            />
                            <Label htmlFor={`required-${question.id}`} className="cursor-pointer">
                              {t('services:customQuestions.required')}
                            </Label>
                          </div>

                          {(question.type === 'select' || question.type === 'checkbox') && (
                            <div className="space-y-2 pl-4 border-l-2 border-gray-200">
                              <div className="flex items-center justify-between">
                                <Label className="text-sm font-medium">
                                  {t('services:customQuestions.options')}
                                </Label>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => addOption(question.id)}
                                  disabled={disabled}
                                  className="h-8"
                                >
                                  <Plus className="h-4 w-4 mr-1" />
                                  {t('services:customQuestions.addOption')}
                                </Button>
                              </div>
                              <div className="space-y-2">
                                {(question.options || []).map((option, optionIndex) => (
                                  <div key={optionIndex} className="flex items-center gap-2">
                                    <Input
                                      value={option}
                                      onChange={(e) =>
                                        updateOption(question.id, optionIndex, e.target.value)
                                      }
                                      placeholder={`${t('services:customQuestions.optionPlaceholder')} ${optionIndex + 1}`}
                                      disabled={disabled}
                                      className="h-9"
                                    />
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => removeOption(question.id, optionIndex)}
                                      disabled={disabled}
                                      className="h-9 w-9 p-0"
                                    >
                                      <Trash2 className="h-4 w-4 text-red-500" />
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {questions.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <HelpCircle className="h-10 w-10 mx-auto mb-3 text-gray-300" />
          <p className="text-sm font-medium">{t('services:customQuestions.noQuestions')}</p>
          <p className="text-xs mt-1">{t('services:customQuestions.noQuestionsHint')}</p>
        </div>
      )}

      <Button
        type="button"
        variant="outline"
        onClick={addQuestion}
        disabled={disabled || questions.length >= 3}
        className="w-full"
      >
        <Plus className="w-4 h-4 mr-2" />
        {t('services:customQuestions.addQuestion')} ({questions.length}/3)
      </Button>
    </div>
  );
}

/**
 * Validates custom questions before submission
 * Returns an error message if validation fails, or null if valid
 */
export function validateCustomQuestions(questions: CustomQuestion[], t: (key: string) => string): string | null {
  for (const question of questions) {
    if (!question.question.trim()) {
      return t('services:customQuestions.validation.questionRequired');
    }

    if ((question.type === 'select' || question.type === 'checkbox') &&
        (!question.options || question.options.length === 0)) {
      return t('services:customQuestions.validation.optionsRequired');
    }

    if ((question.type === 'select' || question.type === 'checkbox') && question.options) {
      const emptyOptions = question.options.filter(opt => !opt.trim());
      if (emptyOptions.length > 0) {
        return t('services:customQuestions.validation.emptyOption');
      }
    }
  }

  return null;
}
